import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Loading } from '@leapeasy/ui-kit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPolicyAction, getPolicyCancelAction } from 'store/actions/propertyActions';
import { getFlaggedCancelReportAction } from 'store/actions/reportActions';
import { getPolicy } from 'store/sagas/APIs/property';
import { fetchAllPaginated } from 'utils/exportPagination';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';
import { buildBuildingBreadcrumbParent, withBreadcrumbParents } from 'utils/breadcrumbNavigation';
import { FlaggedCancellationTable } from 'components/Tables/FlaggedCancellation';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { ApplicationListActiveFilters } from 'components/ApplicationListActiveFilters/ApplicationListActiveFilters';
import {
  buildFlaggedCancellationListFilterChips,
  parseFlaggedCancellationListFilterChipId,
} from 'utils/propertyListActiveFilterChips';
import {
  DEFAULT_FLAGGED_CANCELLATION_LIST_EXTRA_FILTERS,
  FLAGGED_CANCELLATION_LIST_URL_PARAM_KEYS,
  areArraysEqual,
  parseFlaggedCancellationFilters,
  serializeFlaggedCancellationFilters,
} from 'utils/flaggedCancellationListQueryFilters';
import {
  POLICY_LIST_APP_TYPE_FILTER_OPTIONS,
  POLICY_FLAG_FILTER_OPTIONS,
  POLICY_STATUS_FILTER_OPTIONS,
} from 'data/constants/common_constants';

const EMPTY_FLAGGED_CANCEL_FILTER = {
  searchText: '',
  options: {},
  selectedLeaseDate: {},
  selectedFlg: [],
  selectedAppType: [],
  selectedPolicyStatus: [],
};

/** Property portal building detail uses role+id; embedded contexts use legacy buildingId. */
export const FLAGGED_CANCELLATION_POLICY_SCOPE = {
  BUILDING_ID: 'buildingId',
  ROLE_BUILDING: 'roleBuilding',
};

const policyRequestContext = (building, policyScope) => {
  if (!building?.building_id) return {};
  if (policyScope === FLAGGED_CANCELLATION_POLICY_SCOPE.ROLE_BUILDING) {
    return { role: 'building', id: building.building_id };
  }
  return { buildingId: building.building_id };
};

export const CancellationsTab = ({
  building,
  policyScope = FLAGGED_CANCELLATION_POLICY_SCOPE.BUILDING_ID,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousBuildingIdRef = useRef(undefined);

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search ...',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_FLAGGED_CANCELLATION_LIST_EXTRA_FILTERS,
    parseExtraFilters: parseFlaggedCancellationFilters,
    serializeExtraFilters: serializeFlaggedCancellationFilters,
  });

  const [selectedFlg, setSelectedFlg] = useState(() =>
    Array.isArray(extraFilters?.selectedFlg) ? extraFilters.selectedFlg : [],
  );
  const [selectedAppType, setSelectedAppType] = useState(() =>
    Array.isArray(extraFilters?.selectedAppType) ? extraFilters.selectedAppType : [],
  );
  const [selectedPolicyStatus, setSelectedPolicyStatus] = useState(() =>
    Array.isArray(extraFilters?.selectedPolicyStatus) ? extraFilters.selectedPolicyStatus : [],
  );
  const [selectedFlgDraft, setSelectedFlgDraft] = useState(() =>
    Array.isArray(extraFilters?.selectedFlg) ? extraFilters.selectedFlg : [],
  );
  const [selectedAppTypeDraft, setSelectedAppTypeDraft] = useState(() =>
    Array.isArray(extraFilters?.selectedAppType) ? extraFilters.selectedAppType : [],
  );
  const [selectedPolicyStatusDraft, setSelectedPolicyStatusDraft] = useState(() =>
    Array.isArray(extraFilters?.selectedPolicyStatus) ? extraFilters.selectedPolicyStatus : [],
  );
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedFlg = Array.isArray(extraFilters.selectedFlg) ? extraFilters.selectedFlg : [];
    const nextSelectedAppType = Array.isArray(extraFilters.selectedAppType)
      ? extraFilters.selectedAppType
      : [];
    const nextSelectedPolicyStatus = Array.isArray(extraFilters.selectedPolicyStatus)
      ? extraFilters.selectedPolicyStatus
      : [];
    setSelectedFlg((prev) => (areArraysEqual(prev, nextSelectedFlg) ? prev : nextSelectedFlg));
    setSelectedAppType((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedPolicyStatus((prev) =>
      areArraysEqual(prev, nextSelectedPolicyStatus) ? prev : nextSelectedPolicyStatus,
    );
    setSelectedFlgDraft((prev) => (areArraysEqual(prev, nextSelectedFlg) ? prev : nextSelectedFlg));
    setSelectedAppTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedPolicyStatusDraft((prev) =>
      areArraysEqual(prev, nextSelectedPolicyStatus) ? prev : nextSelectedPolicyStatus,
    );
  }, [extraFilters]);

  useLayoutEffect(() => {
    const id = building?.building_id;
    if (!id) return;
    if (previousBuildingIdRef.current !== id) {
      if (previousBuildingIdRef.current !== undefined) {
        const next = new URLSearchParams(searchParams);
        FLAGGED_CANCELLATION_LIST_URL_PARAM_KEYS.forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
      }
      previousBuildingIdRef.current = id;
    }
  }, [building?.building_id, searchParams, setSearchParams]);

  const handleDispatch = useCallback(() => {
    if (!building?.building_id) return;
    dispatch(
      getPolicyCancelAction({
        filter: {
          ...filter,
          selectedLeaseDate: {},
          selectedFlg,
          selectedAppType,
          selectedPolicyStatus,
        },
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions,
        ...policyRequestContext(building, policyScope),
      }),
    );
  }, [
    dispatch,
    building.building_id,
    policyScope,
    filter,
    selectedFlg,
    selectedAppType,
    selectedPolicyStatus,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
  ]);

  useEffect(() => {
    handleDispatch();
  }, [handleDispatch]);

  const successMsg = useSelector((state) => state.getIn(['notification', 'successMsg']));
  const successText = successMsg == null ? '' : String(successMsg);

  useEffect(() => {
    if (!successText || successText === 'loading') return;
    if (!successText.toLowerCase().includes('cancelled')) return;
    handleDispatch();
    dispatch(getFlaggedCancelReportAction());
    if (building?.building_id) {
      dispatch(
        getPolicyAction({
          id: building.building_id,
          role: 'building',
        }),
      );
    }
  }, [successText, dispatch, handleDispatch, building?.building_id]);

  const applyFunc = useCallback(() => {
    setSelectedFlg(selectedFlgDraft);
    setSelectedAppType(selectedAppTypeDraft);
    setSelectedPolicyStatus(selectedPolicyStatusDraft);
    setExtraFilters({
      selectedFlg: selectedFlgDraft,
      selectedAppType: selectedAppTypeDraft,
      selectedPolicyStatus: selectedPolicyStatusDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedFlgDraft,
    selectedAppTypeDraft,
    selectedPolicyStatusDraft,
    setExtraFilters,
    setPagination,
  ]);

  const resetFunc = useCallback(() => {
    setSelectedFlg([]);
    setSelectedAppType([]);
    setSelectedPolicyStatus([]);
    setSelectedFlgDraft([]);
    setSelectedAppTypeDraft([]);
    setSelectedPolicyStatusDraft([]);
    setExtraFilters({ ...DEFAULT_FLAGGED_CANCELLATION_LIST_EXTRA_FILTERS });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const POLICYFLAG = POLICY_FLAG_FILTER_OPTIONS;

  const APPTYPE = POLICY_LIST_APP_TYPE_FILTER_OPTIONS;

  const POLICY_STATUS = POLICY_STATUS_FILTER_OPTIONS;

  const onClickCancelPolicies = useCallback(
    (flaggedCancellation) => {
      const policyId = flaggedCancellation?.app_id;
      if (!policyId) {
        dispatch(failedNotif('Flagged cancellation detail ID is missing.'));
        return;
      }

      const buildingParent = buildBuildingBreadcrumbParent(building, building?.building_id);
      dispatch(
        openDetails({
          type: 'flagged-cancellation',
          data: flaggedCancellation,
        }),
      );

      navigate(`/property/cancel-policies/${policyId}/detail`, {
        state: withBreadcrumbParents(buildingParent ? [buildingParent] : []),
      });
    },
    [dispatch, navigate, building],
  );

  const flaggedCancellations = useSelector((state) => state.getIn(['property', 'policy-cancel']));
  const flaggedCancellationsJSON = flaggedCancellations && flaggedCancellations.toJS();

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(flaggedCancellationsJSON?.total) || 0,
    isLoading: Boolean(flaggedCancellationsJSON?.isLoading),
  });

  useEffect(() => {
    if (!building?.building_id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getPolicy({
          filter: EMPTY_FLAGGED_CANCEL_FILTER,
          cancel: true,
          offset: 0,
          limit: 1,
          sortOptions,
          ...policyRequestContext(building, policyScope),
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortOptions, building?.building_id, policyScope]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(flaggedCancellationsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getPolicy({
            filter: {
              ...filter,
              selectedLeaseDate: {},
              selectedFlg,
              selectedAppType,
              selectedPolicyStatus,
            },
            cancel: true,
            offset,
            limit,
            sortOptions,
            ...policyRequestContext(building, policyScope),
          }),
      });
    } catch (error) {
      throw new Error('Unable to export filtered flagged cancellations right now.');
    }
  }, [
    flaggedCancellationsJSON?.total,
    filter,
    selectedFlg,
    selectedAppType,
    selectedPolicyStatus,
    sortOptions,
    building.building_id,
    policyScope,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords =
      Number(fullExportDatasetTotal) || Number(flaggedCancellationsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getPolicy({
            filter: EMPTY_FLAGGED_CANCEL_FILTER,
            cancel: true,
            offset,
            limit,
            sortOptions,
            ...policyRequestContext(building, policyScope),
          }),
      });
    } catch (error) {
      throw new Error('Unable to export all flagged cancellations right now.');
    }
  }, [
    fullExportDatasetTotal,
    flaggedCancellationsJSON?.total,
    sortOptions,
    building.building_id,
    policyScope,
  ]);

  const listFilterChips = useMemo(
    () =>
      buildFlaggedCancellationListFilterChips({
        selectedFlg,
        selectedAppType,
        selectedPolicyStatus,
        flagOptions: POLICYFLAG,
        appTypeOptions: APPTYPE,
        policyStatusOptions: POLICY_STATUS,
      }),
    [selectedFlg, selectedAppType, selectedPolicyStatus, POLICYFLAG, APPTYPE, POLICY_STATUS],
  );

  const handleRemoveFlaggedCancellationListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseFlaggedCancellationListFilterChipId(chipId);
      if (!parsed) return;

      if (parsed.type === 'flag') {
        const next = selectedFlg.filter((s) => s !== parsed.value);
        setSelectedFlg(next);
        setSelectedFlgDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedFlg: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'appType') {
        const next = selectedAppType.filter((s) => s !== parsed.value);
        setSelectedAppType(next);
        setSelectedAppTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedAppType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'policyStatus') {
        const next = selectedPolicyStatus.filter((s) => s !== parsed.value);
        setSelectedPolicyStatus(next);
        setSelectedPolicyStatusDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedPolicyStatus: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [selectedFlg, selectedAppType, selectedPolicyStatus, setExtraFilters, setPagination],
  );

  const listFilterActiveSummary = useMemo(() => {
    if (!listFilterChips.length) return null;
    return (
      <ApplicationListActiveFilters
        chips={listFilterChips}
        onRemoveChip={handleRemoveFlaggedCancellationListFilterChip}
        onClearAll={resetFunc}
      />
    );
  }, [listFilterChips, handleRemoveFlaggedCancellationListFilterChip, resetFunc]);

  return (
    <Box>
      {flaggedCancellations ? (
        <React.Fragment>
          <CustomBox>
            {flaggedCancellationsJSON.isLoading && (
              <LoadingEffect>
                <Loading size={'large'} />
              </LoadingEffect>
            )}
            <FlaggedCancellationTable
              flaggedCancellations={flaggedCancellationsJSON.data}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              exportFullDatasetTotal={fullExportDatasetTotal}
              dropFilterActiveSummary={listFilterActiveSummary}
              dropFilterActiveCount={listFilterChips.length}
              filter={filter}
              onChangeFilter={setFilter}
              pagination={{
                ...pagination,
                totalItems: flaggedCancellationsJSON.total,
                totalPages: Math.ceil(flaggedCancellationsJSON.total / pagination.rowsPerPage),
              }}
              dropFilter={{
                component: (
                  <DropFilterContainer>
                    <Dropdown
                      onChange={(e) => {
                        setSelectedFlgDraft(e.target.value);
                      }}
                      options={[...POLICYFLAG]}
                      value={selectedFlgDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="All Flags"
                      placeholderFlag={false}
                      label="Flags"
                      style={{ width: '100%' }}
                    />
                    <Dropdown
                      onChange={(e) => {
                        setSelectedPolicyStatusDraft(e.target.value);
                      }}
                      options={[...POLICY_STATUS]}
                      value={selectedPolicyStatusDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="All Types"
                      placeholderFlag={false}
                      label="Policy Status"
                      style={{ width: '100%' }}
                    />
                    <Dropdown
                      onChange={(e) => {
                        setSelectedAppTypeDraft(e.target.value);
                      }}
                      options={[...APPTYPE]}
                      value={selectedAppTypeDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="All Types"
                      placeholderFlag={true}
                      label="Application Type"
                      style={{ width: '100%' }}
                    />
                  </DropFilterContainer>
                ),
                applyFunc: applyFunc,
                resetFunc: resetFunc,
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickCancelPolicies={onClickCancelPolicies}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </Box>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;
